/**
 * /verify-email — where the confirmation link lands.
 *
 * THE ROUTE HAD TO EXIST. sendVerificationEmail builds
 * `${origin}/verify-email?token=...` (lib/accounts.js) and has been sending it
 * since Stage 1, with nothing here to catch it — every one of those links has
 * been landing on the 404 page.
 *
 * NO GUARD, and there must not be one. The token IS the credential: it is
 * signed, typed and names one user, and the common case is a phone's mail app
 * where the site has no cookies at all. A signed-in reader is fine too; the
 * token still decides whose address is confirmed, never the session. See the
 * header of functions/api/auth/verify-email.js.
 *
 * FIRES EXACTLY ONCE PER MOUNT, via a ref rather than the effect's deps.
 * StrictMode double-invokes effects in development, and two concurrent calls
 * would both read email_verified = 0 and both run the guest-history claim
 * behind it — a season rebuild racing itself. The endpoint is safe to call
 * twice in sequence (the second answers alreadyVerified) but not at once.
 *
 * What confirming actually does is the part worth telling them about: it hands
 * over the nights they played as a guest under this address. Access was never
 * gated on it — the account has worked since the moment it was created.
 */

import { useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { apiPost } from '../lib/api.js';
import { useAuth } from '../context/AuthContext.jsx';

/** Total entries claimed across every venue that had any. */
function countLinked(claimed) {
  return (claimed ?? []).reduce((sum, claim) => sum + (claim.linkedEntries ?? 0), 0);
}

const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

export default function VerifyEmail() {
  const [params] = useSearchParams();
  const token = params.get('token');

  const { refresh } = useAuth();

  const [status, setStatus] = useState(token ? 'working' : 'no-token');
  const [result, setResult] = useState(null);
  const [error, setError]   = useState(null);

  const fired = useRef(false);

  useEffect(() => {
    if (!token || fired.current) return;
    fired.current = true;

    (async () => {
      const { data, error: err } = await apiPost('/api/auth/verify-email', { token });

      if (err) {
        // One message covers forged, expired and wrong-type tokens, and the
        // backend has already decided how to word that. Passed through rather
        // than reinterpreted here.
        setError(err);
        setStatus('error');
        return;
      }

      setResult(data);
      setStatus(data.alreadyVerified ? 'already' : 'verified');

      // Only matters when the link was opened on a device that already holds
      // the session: emailVerified has just changed underneath the context, and
      // so has the venues list if a claim added one. A signed-out reader gets a
      // failed refresh, which is the state they were already in.
      await refresh();
    })();
  }, [token, refresh]);

  const linked    = countLinked(result?.claimed);
  const conflicts = result?.conflicts?.length ?? 0;

  return (
    <div className="hd-auth">
      <div className="hd-auth-inner hd-stack">
        <div>
          <div
            className="hd-brand"
            style={{ padding: 0, fontSize: 22, marginBottom: 4 }}
          >
            HOPDARTS
          </div>
          <p className="hd-meta">Confirm your email</p>
        </div>

        {status === 'working' && <p>Confirming your email…</p>}

        {status === 'no-token' && (
          <p>
            This page needs the link from your confirmation email. Open it from
            your inbox rather than typing the address in.
          </p>
        )}

        {status === 'error' && (
          <>
            <div className="hd-badge hd-badge-danger" role="alert">{error}</div>
            <p className="hd-hint">
              Your account still works — confirming only affects guest history,
              never access. Sign in and you can send yourself a fresh link.
            </p>
          </>
        )}

        {status === 'already' && (
          <p>
            That address was already confirmed — nothing more to do. If you
            played as a guest under it, those nights are already yours.
          </p>
        )}

        {status === 'verified' && (
          <>
            <p><strong>Your email is confirmed.</strong></p>

            {linked > 0 ? (
              <p>
                We found {plural(linked, 'night')} you played as a guest under
                this address and added {linked === 1 ? 'it' : 'them'} to your
                account — averages, season points and any records included.
              </p>
            ) : (
              <p className="hd-hint">
                We didn't find any guest nights under this address. Everything
                you play from here on is recorded against your account.
              </p>
            )}

            {conflicts > 0 && (
              <div className="hd-badge hd-badge-info" role="status">
                Some of those nights clash with entries you already had under
                this account, so we've left them for a venue admin to sort out.
              </div>
            )}
          </>
        )}

        <p className="hd-hint">
          <Link to="/profile">Your profile</Link> · <Link to="/login">Sign in</Link>
        </p>
      </div>
    </div>
  );
}
